/**
 * Builds diagram groups for the Reconciliation Diagram tab: 3 columns
 * Invoice (as extracted) → NEX (matched) → Final (reconciled line items).
 */

import type { InvoicePart } from '../hooks/invoice/useGetInvoiceDetail';

export type MatchReason =
    | 'Part number matches'
    | 'Part number corrected (OCR match)'
    | 'Supersession: invoice part matched to current part in NEX'
    | 'Probable match (description/price/vendor)'
    | 'Split to match NEX structure'
    | 'Combined to match NEX structure'
    | 'Normalized to match NEX structure';

export interface DiagramConnector {
    /** Display reason on the arrow (invoice → NEX) */
    reason: MatchReason;
}

export interface DiagramGroup {
    kind: '1:1' | 'expanded' | 'consolidated' | 'normalized';
    /** Column 1: Invoice line(s) exactly as on the invoice (extracted). */
    invoiceLines: InvoicePart[];
    /** Column 2: NEX line(s) matched to this group (partDesc, partNumber, qty, etc.). */
    nexLines: any[];
    /** Column 3: Final reconciled line item(s) after comparing to NEX. */
    finalLines: InvoicePart[];
    /** Match reason for invoice → NEX (one per logical connection, or one for group). */
    connectors: DiagramConnector[];
    /** If this group has a reconciliation flag */
    isFlagged?: boolean;
    /** For sorting by invoice order (original line index). */
    sortKey?: number;
    /** Column 1 display: use these titles (from original extracted) instead of invoiceLines[].title when set. */
    invoiceDescriptions?: string[];
}

interface CorrectionMeta {
    type: 'expanded_to_match_nex' | 'consolidated_to_match_nex' | 'normalized_to_match_nex' | 'part_number_auto_corrected' | 'supersession_auto_corrected';
    partNumber: string;
    newIndices: number[];
    originalLine?: InvoicePart;
    originalLines?: InvoicePart[];
    newLines?: InvoicePart[];
    newLine?: InvoicePart;
    originalPartNumber?: string;
    matchedNexPartNumber?: string;
}

function matchReasonFromCorrectionType(
    type: CorrectionMeta['type'],
    originalPartNumber?: string
): MatchReason {
    switch (type) {
        case 'part_number_auto_corrected':
            return 'Part number corrected (OCR match)';
        case 'supersession_auto_corrected':
            return 'Supersession: invoice part matched to current part in NEX';
        case 'expanded_to_match_nex':
            return 'Split to match NEX structure';
        case 'consolidated_to_match_nex':
            return 'Combined to match NEX structure';
        case 'normalized_to_match_nex':
            return 'Normalized to match NEX structure';
        default:
            return 'Part number matches';
    }
}

/** Find the first index in originalExtracted.lineItems that matches the given line (by part + qty + total). */
function findOriginalIndex(
    originalLineItems: InvoicePart[] | undefined,
    line: InvoicePart,
    fallback: number = 0
): number {
    if (!originalLineItems?.length) return fallback;
    const idx = originalLineItems.findIndex(
        (l) =>
            (l.partNumber ?? '') === (line.partNumber ?? '') &&
            (l.qty ?? 0) === (line.qty ?? 0) &&
            (l.totalBuyPrice ?? 0) === (line.totalBuyPrice ?? 0)
    );
    return idx >= 0 ? idx : fallback;
}

/** Build diagram groups ordered by invoice line order. */
export function buildReconciliationDiagram(
    lineItems: InvoicePart[],
    originalExtractedLineItems: InvoicePart[] | undefined,
    lineItemCorrectionMetadata: CorrectionMeta[] | undefined,
    reconciliationIssues: {
        flaggedParts?: Array<{ partIndex: number }>;
        probableMatches?: Array<{ partIndex: number; invoicePart: InvoicePart; nexLine: any }>;
        matchedNexLines?: Array<{ partIndex: number; nexLine: any }>;
    } | undefined
): { groups: DiagramGroup[]; unusedNexLines: any[] } {
    const groups: DiagramGroup[] = [];
    const currentIndicesInCorrections = new Set<number>();
    const probableMatchByPartIndex = new Map<number, { invoicePart: InvoicePart; nexLine: any }>();
    (reconciliationIssues?.probableMatches ?? []).forEach((m) => {
        probableMatchByPartIndex.set(m.partIndex, { invoicePart: m.invoicePart, nexLine: m.nexLine });
    });
    const matchedNexLinesByIndex = new Map<number, any>();
    (reconciliationIssues?.matchedNexLines ?? []).forEach((m) => {
        matchedNexLinesByIndex.set(m.partIndex, m.nexLine);
    });
    const flaggedSet = new Set((reconciliationIssues?.flaggedParts ?? []).map((f) => f.partIndex));

    // Corrections: each yields one group
    (lineItemCorrectionMetadata ?? []).forEach((correction) => {
        const reason = matchReasonFromCorrectionType(correction.type, correction.originalPartNumber);
        (correction.newIndices ?? []).forEach((i) => currentIndicesInCorrections.add(i));

        if (correction.type === 'expanded_to_match_nex' && correction.originalLine && correction.newLines?.length) {
            (correction.newIndices ?? []).forEach((i) => currentIndicesInCorrections.add(i));
            const sortKey = findOriginalIndex(originalExtractedLineItems, correction.originalLine);
            const asReadLine = originalExtractedLineItems?.[sortKey] ?? correction.originalLine;
            const invoiceLines = [asReadLine];
            const finalLines = correction.newLines;
            const newIndices = correction.newIndices ?? [];
            const nexLines = newIndices.map((i) => matchedNexLinesByIndex.get(i)).filter((nl): nl is any => nl != null);
            groups.push({
                kind: 'expanded',
                invoiceLines,
                nexLines,
                finalLines,
                connectors: finalLines.map(() => ({ reason })),
                isFlagged: finalLines.some((_, i) => flaggedSet.has(newIndices[i] ?? -1)),
                sortKey,
                invoiceDescriptions: [asReadLine.title ?? '—'],
            });
            return;
        }

        if (
            (correction.type === 'consolidated_to_match_nex' || correction.type === 'normalized_to_match_nex') &&
            (correction.originalLines?.length ?? 0) > 0
        ) {
            (correction.newIndices ?? []).forEach((i) => currentIndicesInCorrections.add(i));
            const origLines = correction.originalLines ?? [];
            const finalLines = correction.newLines ?? (correction.newLine ? [correction.newLine] : []);
            const sortKey = Math.min(
                ...origLines.map((l) => findOriginalIndex(originalExtractedLineItems, l))
            );
            const newIndices = correction.newIndices ?? [];
            const singleNex = finalLines.length > 0 ? matchedNexLinesByIndex.get(newIndices[0]) : undefined;
            const nexLines = singleNex != null ? [singleNex] : [];
            // Left column = as read from invoice (original extracted at consecutive indices)
            const invoiceLines = origLines.map((l, j) => originalExtractedLineItems?.[sortKey + j] ?? l);
            groups.push({
                kind: correction.type === 'consolidated_to_match_nex' ? 'consolidated' : 'normalized',
                invoiceLines,
                nexLines,
                finalLines,
                connectors: finalLines.length > 0 ? [{ reason }] : invoiceLines.map(() => ({ reason })),
                isFlagged: newIndices.some((i) => flaggedSet.has(i)),
                sortKey,
                invoiceDescriptions: invoiceLines.map((l) => l.title ?? '—'),
            });
            return;
        }

        // part_number_auto_corrected / supersession_auto_corrected: 1:1 at current index
        let idx = correction.newIndices?.[0];
        if (idx === undefined && correction.partNumber) {
            idx = lineItems.findIndex((l) => (l.partNumber ?? '') === correction.partNumber);
        }
        if (idx === undefined || idx < 0) idx = 0;
        currentIndicesInCorrections.add(idx);
        const currentLine = lineItems[idx];
        if (currentLine) {
            // Left column = as read from invoice (original extracted at this index); right = corrected
            let asReadLine = originalExtractedLineItems?.[idx] ?? correction.originalLine ?? correction.originalLines?.[0] ?? currentLine;
            if (asReadLine === currentLine && correction.originalPartNumber != null) {
                asReadLine = { ...currentLine, partNumber: correction.originalPartNumber };
            }
            const sortKey = findOriginalIndex(originalExtractedLineItems, asReadLine, idx);
            const nexLine = matchedNexLinesByIndex.get(idx);
            groups.push({
                kind: '1:1',
                invoiceLines: [asReadLine],
                nexLines: nexLine != null ? [nexLine] : [],
                finalLines: [currentLine],
                connectors: [{ reason }],
                isFlagged: flaggedSet.has(idx),
                sortKey,
                invoiceDescriptions: [asReadLine.title ?? '—'],
            });
        }
    });

    // 1:1 groups for current line indices not in any correction
    for (let i = 0; i < lineItems.length; i++) {
        if (currentIndicesInCorrections.has(i)) continue;
        const line = lineItems[i];
        const prob = probableMatchByPartIndex.get(i);
        const reason: MatchReason = prob
            ? 'Probable match (description/price/vendor)'
            : 'Part number matches';
        const sortKey = originalExtractedLineItems
            ? prob
                ? findOriginalIndex(originalExtractedLineItems, prob.invoicePart, i)
                : findOriginalIndex(originalExtractedLineItems, line, i)
            : i;
        // Left column = as read from invoice (original extracted at this index)
        const asReadLine = originalExtractedLineItems?.[i] ?? prob?.invoicePart ?? line;
        const nexLine = matchedNexLinesByIndex.get(i) ?? prob?.nexLine;
        groups.push({
            kind: '1:1',
            invoiceLines: [asReadLine],
            nexLines: nexLine != null ? [nexLine] : [],
            finalLines: [line],
            connectors: [{ reason }],
            isFlagged: flaggedSet.has(i),
            sortKey,
            invoiceDescriptions: [asReadLine.title ?? '—'],
        });
    }

    // Sort by original invoice order (sortKey = original index)
    groups.sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0));

    // Unused NEX: we don't have full NEX list on client; return empty. Backend can add allNexLines + used keys later.
    const unusedNexLines: any[] = [];

    return { groups, unusedNexLines };
}
